import { FC, SyntheticEvent, useRef } from 'react'

import { IconAlert } from '@avantstay/backoffice-vectors'

import { Button } from '../../components/Button'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import * as S from './ConfirmActionFooter.styles'
import {
  ConfirmActionFooterContentProps,
  ConfirmActionFooterTheme,
} from './ConfirmActionFooter.types'

const getMainThemeButton = (theme: ConfirmActionFooterTheme) => {
  if (theme === ConfirmActionFooterTheme.danger) {
    return Button.Danger
  }
  return Button.Primary
}

// Keep this export clean, free of wrapping HOCs
export const ConfirmActionFooterContent: FC<ConfirmActionFooterContentProps> = ({
  cancelText = 'No',
  confirmButtonType = 'button',
  confirmText = 'Yes',
  containerClassName,
  isActionLoading,
  message,
  onConfirm,
  onCancel,
  theme = ConfirmActionFooterTheme.primary,
  title,
  shouldDisplayActionContainer,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const MainButton = getMainThemeButton(theme)

  useOnClickOutside(containerRef, onCancel)

  const handleOnConfirm = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (isActionLoading) {
      event.preventDefault()
    }
    onConfirm()
  }

  const handleOnCancel = (event: SyntheticEvent<HTMLButtonElement>) => {
    if (isActionLoading) {
      event.preventDefault()
    }
    onCancel()
  }

  return (
    <S.ConfirmActionContentContainer
      className={containerClassName}
      ref={containerRef}
      shouldDisplayActionContainer={shouldDisplayActionContainer}
    >
      <S.ContentContainer>
        {title && (
          <S.TitleHeader theme={theme}>
            <IconAlert />
            <S.Title data-testid="confirmation-action-footer-title">{title}</S.Title>
          </S.TitleHeader>
        )}
        <S.Message data-testid="confirmation-action-footer-message">{message}</S.Message>
        <S.ActionContainer>
          {confirmText && (
            <MainButton
              onClick={handleOnConfirm}
              data-testid="confirm-button-id"
              type={confirmButtonType}
              loading={isActionLoading}
            >
              {confirmText}
            </MainButton>
          )}
          <Button.Secondary
            onClick={handleOnCancel}
            disabled={isActionLoading}
            data-testid="cancel-button-id"
            type="button"
          >
            {cancelText}
          </Button.Secondary>
        </S.ActionContainer>
      </S.ContentContainer>
    </S.ConfirmActionContentContainer>
  )
}
